import {Component, Inject, OnInit, Optional} from '@angular/core';
import {FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators} from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle
} from '@angular/material/dialog';
import {MatError, MatFormField, MatHint, MatLabel} from '@angular/material/form-field';
import {MatInput} from '@angular/material/input';
import {MatButton, MatIconButton} from '@angular/material/button';
import {MatProgressSpinner} from '@angular/material/progress-spinner';
import {MatIcon} from '@angular/material/icon';
import {MatSnackBar} from '@angular/material/snack-bar';

import {NGXLogger} from 'ngx-logger';
import {FeedbackService} from '@twpub/core/services/feedback.service';
import {FeedbackContext, FeedbackPage} from '@twpub/core/models';
import {SecurityConfigService} from '@twpub/shared/services/security-config.service';

@Component({
  selector: 'pub-feedback-dialog',
  templateUrl: './feedback-dialog.component.html',
  styleUrls: ['./feedback-dialog.component.scss'],
  standalone: true,
  imports: [
    MatDialogTitle, MatDialogContent, MatDialogActions, FormsModule, ReactiveFormsModule, MatFormField, MatLabel,
    MatInput, MatError, MatHint, MatButton, MatIconButton, MatProgressSpinner, MatIcon
  ]
})
export class FeedbackDialogComponent implements OnInit {
  feedbackForm: FormGroup;
  config: FeedbackPage | null = null;
  loading = true;
  submitting = false;
  selectedFile: File | null = null;
  fileError: string | null = null;

  // i18n labels
  titleLabel = $localize`:@@feedback.title:Send Feedback`;
  nameLabel = $localize`:@@feedback.name:Your Name`;
  emailLabel = $localize`:@@feedback.email:Your Email`;
  subjectLabel = $localize`:@@feedback.subject:Subject`;
  messageLabel = $localize`:@@feedback.message:Message`;
  attachmentLabel = $localize`:@@feedback.attachment:Attachment`;
  submitLabel = $localize`:@@feedback.submit:Send`;
  cancelLabel = $localize`:@@feedback.cancel:Cancel`;
  requiredError = $localize`:@@feedback.error.required:This field is required`;
  emailError = $localize`:@@feedback.error.email:Please enter a valid email address`;
  fileTooLargeError = $localize`:@@feedback.error.fileTooLarge:File is too large. Maximum size is`;
  successMessage = $localize`:@@feedback.success:Thank you for your feedback!`;
  queuedMessage = $localize`:@@feedback.queued:Your feedback has been queued and will be sent when the server is available.`;
  errorMessage = $localize`:@@feedback.error:Failed to send feedback. Please try again later.`;
  configErrorMessage = $localize`:@@feedback.configError:Feedback is currently unavailable. Please try again later.`;
  loadingLabel = $localize`:@@feedback.loading:Loading...`;

  constructor(
    private logger: NGXLogger,
    private fb: FormBuilder,
    public feedbackService: FeedbackService,
    private securityConfigService: SecurityConfigService,
    private dialogRef: MatDialogRef<FeedbackDialogComponent>,
    private snackBar: MatSnackBar,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: { context?: FeedbackContext } | null = null
  ) {
    this.feedbackForm = this.fb.group({
      name: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      subject: ['', Validators.required],
      message: ['', Validators.required]
    });
  }

  ngOnInit(): void {
    this.feedbackService.getFeedbackConfig().subscribe({
      next: (config) => {
        this.config = config;
        this.loading = false;
        if (config) {
          // Pre-fill subject with default
          if (config.defaultSubject) {
            this.feedbackForm.patchValue({subject: config.defaultSubject});
          }
        }
        // Pre-fill name and email from authenticated user (SSO/Keycloak/external)
        this.prefillFromUser();
      },
      error: (err) => {
        this.logger.error('Error loading feedback config:', err);
        this.loading = false;
      }
    });
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      this.fileError = null;

      // Validate file size
      if (this.config && file.size > this.config.maxAttachmentSize) {
        this.fileError = `${this.fileTooLargeError} ${this.feedbackService.formatFileSize(this.config.maxAttachmentSize)}`;
        this.selectedFile = null;
        input.value = ''; // Reset file input
        return;
      }

      this.selectedFile = file;
    }
  }

  removeFile(): void {
    this.selectedFile = null;
    this.fileError = null;
  }

  getMaxFileSizeFormatted(): string {
    return this.config ? this.feedbackService.formatFileSize(this.config.maxAttachmentSize) : '';
  }

  submit(): void {
    if (this.feedbackForm.invalid) {
      this.feedbackForm.markAllAsTouched();
      return;
    }

    this.submitting = true;
    const {name, email, subject, message} = this.feedbackForm.value;

    let fullMessage = message;
    if (this.data?.context?.entries?.length) {
      const contextLines = this.data.context.entries
        .map(e => `${e.label}: ${e.value}`)
        .join('\n');
      fullMessage = contextLines + '\n\n' + message;
    }

    this.feedbackService.submitFeedback(name, email, subject, fullMessage, this.selectedFile ?? undefined).subscribe({
      next: (response) => {
        this.submitting = false;
        if (response.success) {
          const msg = response.queued ? this.queuedMessage : this.successMessage;
          this.snackBar.open(msg, undefined, {
            duration: 5000,
            horizontalPosition: 'center',
            verticalPosition: 'top',
            panelClass: response.queued ? ['snackbar-warning'] : ['snackbar-success']
          });
          this.dialogRef.close(true);
        } else {
          this.snackBar.open(response.message || this.errorMessage, undefined, {
            duration: 5000,
            horizontalPosition: 'center',
            verticalPosition: 'top',
            panelClass: ['snackbar-error']
          });
        }
      },
      error: (err) => {
        this.logger.error('Error submitting feedback:', err);
        this.submitting = false;
        this.snackBar.open(this.errorMessage, undefined, {
          duration: 5000,
          horizontalPosition: 'center',
          verticalPosition: 'top',
          panelClass: ['snackbar-error']
        });
      }
    });
  }

  private prefillFromUser(): void {
    if (this.securityConfigService.isUserAuthenticated()) {
      const user = this.securityConfigService.getUser();
      if (user) {
        const patch: Record<string, string> = {};
        if (user.email) {
          patch.email = user.email;
        }
        if (user.name) {
          patch.name = user.name;
        }
        if (Object.keys(patch).length > 0) {
          this.feedbackForm.patchValue(patch);
        }
      }
    }
  }

  cancel(): void {
    this.dialogRef.close(false);
  }

  get isConfigAvailable(): boolean {
    return this.config !== null && this.config.enabled;
  }
}
